<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useChecklistsStore, type ItemToCheck, type ItemCondition, type ChecklistType } from '../stores/checklists'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const checklistsStore = useChecklistsStore()

// View mode
const viewMode = ref<'list' | 'active' | 'view'>('list')
const checklistId = computed(() => route.params.id as string | undefined)

// New checklist form
const showNewChecklistModal = ref(false)
const newChecklistType = ref<ChecklistType>('checkout')
const newChecklistNotes = ref('')

// Item check form
const currentItemIndex = ref(0)
const itemCondition = ref<ItemCondition>('good')
const itemQuantity = ref<number>(0)
const itemNotes = ref('')
const itemPhotoKey = ref('')
const itemPhotoPreview = ref<string | null>(null)
const uploading = ref(false)
const saving = ref(false)

const API_BASE = import.meta.env.VITE_API_URL || ''

const currentItem = computed(() => {
  const items = checklistsStore.uncheckedItems
  return items[currentItemIndex.value] || null
})

const checklistTypeLabels: Record<ChecklistType, string> = {
  checkout: 'Checkout Inspection',
  checkin: 'Check-in Inspection',
  inspection: 'General Inspection',
}

const conditionOptions: { value: ItemCondition; label: string }[] = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing', label: 'Missing' },
]

onMounted(async () => {
  if (checklistId.value) {
    // Viewing a specific checklist
    await checklistsStore.fetchChecklist(checklistId.value)
    await checklistsStore.fetchItemsToCheck()
    viewMode.value = checklistsStore.currentChecklist?.status === 'in_progress' ? 'active' : 'view'
  } else {
    await checklistsStore.fetchChecklists()
  }
})

watch(checklistId, async (newId) => {
  if (newId) {
    await checklistsStore.fetchChecklist(newId)
    await checklistsStore.fetchItemsToCheck()
    viewMode.value = checklistsStore.currentChecklist?.status === 'in_progress' ? 'active' : 'view'
  } else {
    checklistsStore.clearCurrent()
    viewMode.value = 'list'
    await checklistsStore.fetchChecklists()
  }
})

async function startNewChecklist() {
  try {
    const checklist = await checklistsStore.createChecklist({
      checklist_type: newChecklistType.value,
      notes: newChecklistNotes.value || undefined,
    })
    showNewChecklistModal.value = false
    newChecklistNotes.value = ''
    router.push(`/checklists/${checklist.id}`)
  } catch (err) {
    console.error('Failed to create checklist:', err)
  }
}

function resetItemForm() {
  itemCondition.value = 'good'
  itemQuantity.value = currentItem.value?.current_quantity ?? 0
  itemNotes.value = ''
  itemPhotoKey.value = ''
  itemPhotoPreview.value = null
}

watch(currentItem, (newItem) => {
  if (newItem) {
    resetItemForm()
    if (newItem.item_type === 'consumable') {
      itemQuantity.value = newItem.current_quantity ?? 0
    }
  }
})

async function handlePhotoSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  // Validate
  if (!file.type.startsWith('image/')) {
    alert('Please select an image file')
    return
  }
  if (file.size > 10 * 1024 * 1024) {
    alert('Image must be less than 10MB')
    return
  }

  // Show preview
  itemPhotoPreview.value = URL.createObjectURL(file)
  uploading.value = true

  try {
    const { uploadUrl, key } = await checklistsStore.getUploadUrl(file.name, file.type)
    await checklistsStore.uploadPhoto(uploadUrl, file)
    itemPhotoKey.value = key
  } catch (err) {
    console.error('Upload failed:', err)
    alert('Failed to upload photo')
    itemPhotoPreview.value = null
  } finally {
    uploading.value = false
    input.value = ''
  }
}

async function saveItemCheck() {
  if (!currentItem.value || !checklistsStore.currentChecklist) return

  saving.value = true
  try {
    await checklistsStore.addItemCheck(checklistsStore.currentChecklist.id, {
      item_type: currentItem.value.item_type,
      item_id: currentItem.value.id,
      photo_key: itemPhotoKey.value || undefined,
      condition: currentItem.value.item_type === 'inventory' ? itemCondition.value : undefined,
      quantity_recorded: currentItem.value.item_type === 'consumable' ? itemQuantity.value : undefined,
      notes: itemNotes.value || undefined,
    })

    // Move to next item or complete
    if (checklistsStore.uncheckedItems.length === 0) {
      // All items checked
      await completeChecklist()
    } else {
      currentItemIndex.value = 0
      resetItemForm()
    }
  } catch (err) {
    console.error('Failed to save item check:', err)
  } finally {
    saving.value = false
  }
}

function skipItem() {
  if (currentItemIndex.value < checklistsStore.uncheckedItems.length - 1) {
    currentItemIndex.value++
  } else {
    currentItemIndex.value = 0
  }
  resetItemForm()
}

async function completeChecklist() {
  if (!checklistsStore.currentChecklist) return

  if (checklistsStore.uncheckedItems.length > 0) {
    if (!confirm(`${checklistsStore.uncheckedItems.length} items haven't been checked. Complete anyway?`)) {
      return
    }
  }

  try {
    await checklistsStore.completeChecklist(checklistsStore.currentChecklist.id)
    viewMode.value = 'view'
  } catch (err) {
    console.error('Failed to complete checklist:', err)
  }
}

async function deleteChecklist(id: string) {
  if (!confirm('Delete this checklist? This cannot be undone.')) return

  try {
    await checklistsStore.deleteChecklist(id)
    if (checklistId.value) {
      router.push('/checklists')
    }
  } catch (err) {
    console.error('Failed to delete:', err)
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString()
}

function getConditionColor(condition: string | null): string {
  switch (condition) {
    case 'excellent': return 'bg-green-100 text-green-800'
    case 'good': return 'bg-blue-100 text-blue-800'
    case 'fair': return 'bg-yellow-100 text-yellow-800'
    case 'poor': return 'bg-orange-100 text-orange-800'
    case 'damaged': return 'bg-red-100 text-red-800'
    case 'missing': return 'bg-gray-100 text-gray-800'
    default: return 'bg-gray-100 text-gray-600'
  }
}
</script>

<template>
  <div class="max-w-4xl mx-auto p-4 md:p-6">
    <!-- List View -->
    <template v-if="viewMode === 'list' && !checklistId">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">Turnover Checklists</h1>
          <p class="text-gray-600">Document property condition between stays</p>
        </div>
        <button @click="showNewChecklistModal = true" class="btn btn-primary">
          + Start Checklist
        </button>
      </div>

      <!-- In Progress -->
      <div v-if="checklistsStore.inProgressChecklists.length > 0" class="mb-8">
        <h2 class="text-lg font-semibold text-gray-700 mb-3">In Progress</h2>
        <div class="space-y-3">
          <div
            v-for="checklist in checklistsStore.inProgressChecklists"
            :key="checklist.id"
            class="card flex items-center justify-between"
          >
            <div>
              <h3 class="font-medium text-gray-800">{{ checklistTypeLabels[checklist.checklist_type] }}</h3>
              <p class="text-sm text-gray-500">
                Started {{ formatDate(checklist.started_at) }} by {{ checklist.started_by_name }}
              </p>
            </div>
            <div class="flex gap-2">
              <router-link :to="`/checklists/${checklist.id}`" class="btn btn-primary btn-sm">
                Continue
              </router-link>
              <button @click="deleteChecklist(checklist.id)" class="btn btn-sm btn-danger">
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Completed -->
      <div>
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Completed</h2>
        <div v-if="checklistsStore.completedChecklists.length === 0" class="card text-center py-8 text-gray-500">
          No completed checklists yet
        </div>
        <div v-else class="space-y-3">
          <div
            v-for="checklist in checklistsStore.completedChecklists"
            :key="checklist.id"
            class="card flex items-center justify-between cursor-pointer hover:shadow-md"
            @click="router.push(`/checklists/${checklist.id}`)"
          >
            <div>
              <h3 class="font-medium text-gray-800">{{ checklistTypeLabels[checklist.checklist_type] }}</h3>
              <p class="text-sm text-gray-500">
                Completed {{ formatDate(checklist.completed_at!) }} by {{ checklist.started_by_name }}
              </p>
            </div>
            <span class="text-green-600 text-sm font-medium">View</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Active Checklist View -->
    <template v-else-if="viewMode === 'active' && checklistsStore.currentChecklist">
      <div class="mb-6">
        <router-link to="/checklists" class="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
          &larr; Back to Checklists
        </router-link>
        <div class="flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-800">
            {{ checklistTypeLabels[checklistsStore.currentChecklist.checklist_type] }}
          </h1>
          <button @click="completeChecklist" class="btn btn-success">
            Complete Checklist
          </button>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="card mb-6">
        <div class="flex justify-between text-sm mb-2">
          <span>Progress</span>
          <span>{{ checklistsStore.currentItems.length }} / {{ checklistsStore.allItemsToCheck.length }} items</span>
        </div>
        <div class="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            class="h-full bg-primary-500 transition-all"
            :style="{ width: `${checklistsStore.progress}%` }"
          ></div>
        </div>
      </div>

      <!-- Current Item to Check -->
      <div v-if="currentItem" class="card">
        <div class="flex items-start gap-4 mb-4">
          <!-- Item Photo -->
          <div class="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
            <img
              v-if="currentItem.photo_url"
              :src="`${API_BASE}${currentItem.photo_url}`"
              :alt="currentItem.name"
              class="w-full h-full object-cover"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
              <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>

          <div class="flex-1">
            <h2 class="text-xl font-bold text-gray-800">{{ currentItem.name }}</h2>
            <p class="text-gray-500 text-sm">
              {{ currentItem.location_name || 'No location' }}
              <span class="mx-2">|</span>
              {{ currentItem.item_type === 'inventory' ? 'Inventory Item' : 'Consumable' }}
            </p>
          </div>
        </div>

        <!-- Condition (for inventory) -->
        <div v-if="currentItem.item_type === 'inventory'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Condition</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="opt in conditionOptions"
              :key="opt.value"
              @click="itemCondition = opt.value"
              :class="[
                'px-4 py-2 rounded-lg border transition-colors',
                itemCondition === opt.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-300 hover:border-gray-400'
              ]"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Quantity (for consumables) -->
        <div v-if="currentItem.item_type === 'consumable'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Quantity {{ currentItem.unit ? `(${currentItem.unit})` : '' }}
          </label>
          <div class="flex items-center gap-4">
            <input
              v-model.number="itemQuantity"
              type="number"
              min="0"
              :max="currentItem.max_quantity"
              class="input w-32"
            />
            <span class="text-gray-500 text-sm">
              Max: {{ currentItem.max_quantity }}
            </span>
          </div>
        </div>

        <!-- Photo Capture -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Photo Evidence</label>
          <div v-if="itemPhotoPreview" class="relative w-48 h-36 mb-2">
            <img :src="itemPhotoPreview" class="w-full h-full object-cover rounded-lg" />
            <button
              @click="itemPhotoPreview = null; itemPhotoKey = ''"
              class="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full text-sm"
            >
              &times;
            </button>
            <div v-if="uploading" class="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
              <span class="text-white text-sm">Uploading...</span>
            </div>
          </div>
          <label v-else class="block">
            <span class="btn btn-sm cursor-pointer">
              Take/Select Photo
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              class="hidden"
              @change="handlePhotoSelect"
            />
          </label>
        </div>

        <!-- Notes -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
          <textarea v-model="itemNotes" class="input" rows="2" placeholder="Any observations..."></textarea>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 pt-4 border-t">
          <button @click="skipItem" class="btn flex-1">
            Skip
          </button>
          <button
            @click="saveItemCheck"
            class="btn btn-primary flex-1"
            :disabled="saving || uploading"
          >
            {{ saving ? 'Saving...' : 'Save & Next' }}
          </button>
        </div>
      </div>

      <!-- All Items Checked -->
      <div v-else class="card text-center py-12">
        <div class="text-5xl mb-4">&#9989;</div>
        <h2 class="text-xl font-bold text-gray-800 mb-2">All Items Checked!</h2>
        <p class="text-gray-600 mb-6">You've documented all items in the checklist.</p>
        <button @click="completeChecklist" class="btn btn-success">
          Complete Checklist
        </button>
      </div>
    </template>

    <!-- View Completed Checklist -->
    <template v-else-if="viewMode === 'view' && checklistsStore.currentChecklist">
      <div class="mb-6">
        <router-link to="/checklists" class="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
          &larr; Back to Checklists
        </router-link>
        <h1 class="text-2xl font-bold text-gray-800">
          {{ checklistTypeLabels[checklistsStore.currentChecklist.checklist_type] }}
        </h1>
        <p class="text-gray-500">
          Completed {{ formatDate(checklistsStore.currentChecklist.completed_at!) }}
          by {{ checklistsStore.currentChecklist.started_by_name }}
        </p>
      </div>

      <!-- Summary -->
      <div class="card mb-6">
        <h2 class="font-semibold text-gray-700 mb-3">Summary</h2>
        <p class="text-gray-600">{{ checklistsStore.currentItems.length }} items documented</p>
        <p v-if="checklistsStore.currentChecklist.notes" class="text-gray-600 mt-2">
          <strong>Notes:</strong> {{ checklistsStore.currentChecklist.notes }}
        </p>
      </div>

      <!-- Checked Items -->
      <div class="space-y-4">
        <div
          v-for="item in checklistsStore.currentItems"
          :key="item.id"
          class="card"
        >
          <div class="flex gap-4">
            <!-- Photo -->
            <div class="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              <img
                v-if="item.photo_url"
                :src="`${API_BASE}${item.photo_url}`"
                :alt="item.item_name"
                class="w-full h-full object-cover"
              />
              <div v-else class="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                No photo
              </div>
            </div>

            <div class="flex-1">
              <h3 class="font-medium text-gray-800">{{ item.item_name }}</h3>
              <p class="text-sm text-gray-500">{{ item.item_location_name }}</p>

              <div class="flex flex-wrap gap-2 mt-2">
                <span
                  v-if="item.condition"
                  :class="['px-2 py-0.5 rounded text-xs', getConditionColor(item.condition)]"
                >
                  {{ item.condition }}
                </span>
                <span v-if="item.quantity_recorded !== null" class="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                  Qty: {{ item.quantity_recorded }}
                </span>
              </div>

              <p v-if="item.notes" class="text-sm text-gray-600 mt-2">{{ item.notes }}</p>
              <p class="text-xs text-gray-400 mt-1">Checked {{ formatDate(item.checked_at) }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- New Checklist Modal -->
    <div
      v-if="showNewChecklistModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="showNewChecklistModal = false"
    >
      <div class="card max-w-md w-full">
        <h2 class="text-xl font-bold text-gray-800 mb-4">Start New Checklist</h2>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Checklist Type</label>
            <select v-model="newChecklistType" class="input">
              <option value="checkout">Checkout Inspection</option>
              <option value="checkin">Check-in Inspection</option>
              <option value="inspection">General Inspection</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
            <textarea v-model="newChecklistNotes" class="input" rows="2" placeholder="Any notes about this inspection..."></textarea>
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button @click="showNewChecklistModal = false" class="btn flex-1">Cancel</button>
          <button @click="startNewChecklist" class="btn btn-primary flex-1">Start</button>
        </div>
      </div>
    </div>
  </div>
</template>
